import { type BigDecimal, absoluteValue, plus } from '@0xtorch/big-decimal'
import type { Action, Transfer } from '@0xtorch/core'
import {
  ApiSecretRequiredException,
  AssetNotFoundException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { bybitAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

// README 記載の ignore types
const ignoreTypes = new Set([
  'ADL',
  'DELIVERY',
  'FIXED_STAKING_REFUND',
  'FIXED_STAKING_SUBSCRIPTION',
  'FLEXIBLE_STAKING_REDEMPTION',
  'FLEXIBLE_STAKING_REFUND',
  'FLEXIBLE_STAKING_SUBSCRIPTION',
  'LIQUIDATION',
  'LOANS_ASSET_REDEMPTION',
  'LOANS_PLEDGE_ASSET',
  'SETTLEMENT',
  'TRADE',
  'TRANSFER_IN',
  'TRANSFER_OUT',
])

// CURRENCY_BUY / CURRENCY_SELL を CONVERT に正規化
const parseType = (type: string): string => {
  if (type === 'CURRENCY_BUY' || type === 'CURRENCY_SELL') {
    return 'CONVERT'
  }
  return type
}

export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  const response = await client.run({
    method: 'GET',
    path: '/v5/account/transaction-log',
    query: {
      limit: config.limit.toString(),
      ...(from ? { startTime: from.toString() } : {}),
      ...(to ? { endTime: to.toString() } : {}),
    },
    responseSchema,
  })

  // transactionTime 降順ソート
  const sortedItems = response.result.list.sort(
    (a, b) => Number(b.transactionTime) - Number(a.transactionTime),
  )

  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const actions: Action[] = []
  const sources: string[] = [] // 仕様上空

  // (transactionTime, parsedType) でグルーピング
  const grouped = new Map<string, typeof sortedItems>()

  for (const item of sortedItems) {
    const ts = Number(item.transactionTime)

    // fetchedFrom / fetchedTo 更新
    fetchedFrom = fetchedFrom === undefined ? ts : Math.min(fetchedFrom, ts)
    fetchedTo = fetchedTo === undefined ? ts : Math.max(fetchedTo, ts)

    const parsed = parseType(item.type)

    if (ignoreTypes.has(parsed)) {
      continue
    }

    const key = `${ts}_${parsed}`
    const list = grouped.get(key) || []
    list.push(item)
    grouped.set(key, list)
  }

  // 各グループ処理
  for (const [key, items] of grouped) {
    if (items.length === 0) continue
    const [tsStr, ...typeParts] = key.split('_')
    const ts = Number(tsStr)
    const parsedType = typeParts.join('_')

    switch (parsedType) {
      // trade
      case 'CONVERT': {
        // coin ごとに cashFlow 集計
        const coinAmounts = new Map<string, BigDecimal>()
        for (const item of items) {
          const existing = coinAmounts.get(item.currency)
          if (existing) {
            coinAmounts.set(item.currency, plus(existing, item.cashFlow))
          } else {
            coinAmounts.set(item.currency, item.cashFlow)
          }
        }

        // 正と負の両方が存在するか検証
        const hasPositive = Array.from(coinAmounts.values()).some(
          (v) => v.value > 0n,
        )
        const hasNegative = Array.from(coinAmounts.values()).some(
          (v) => v.value < 0n,
        )
        if (!hasPositive || !hasNegative) {
          throw new Error(
            'CONVERT group does not contain both positive and negative cashFlow',
          )
        }

        const transfers: Transfer[] = []
        for (const [coin, amount] of coinAmounts) {
          if (amount.value === 0n) continue
          const asset = getAsset(coin) || undefined
          if (!asset) {
            throw new AssetNotFoundException(coin)
          }
          transfers.push({
            asset,
            amount: absoluteValue(amount),
            direction: amount.value > 0n ? 'in' : 'out',
          })
        }

        actions.push({
          comment: `ID: ${items.map((i) => i.id).join(' / ')}`,
          evidence: 'system-rule',
          order: 0,
          source: `${bybitAccountCategory}_${config.type}_Trade_${ts}`,
          type: 'trade',
          timestamp: ts,
          transfers,
        })
        break
      }
      // borrow
      case 'LOANS_BORROW_FUNDS': {
        for (const item of items) {
          if (item.cashFlow.value > 0n) {
            const asset = getAsset(item.currency)
            if (!asset) throw new AssetNotFoundException(item.currency)
            actions.push({
              comment: item.type,
              evidence: 'system-rule',
              order: 0,
              loanId: bybitAccountCategory,
              source: `${bybitAccountCategory}_${config.type}_${item.id}`,
              type: 'borrow',
              timestamp: Number(item.transactionTime),
              transfers: [
                {
                  asset,
                  amount: item.cashFlow,
                  direction: 'in',
                },
              ],
            })
          } else if (item.cashFlow.value < 0n) {
            throw new Error('cashFlow cannot be negative for borrow')
          }
        }
        break
      }
      // repayment
      case 'LOANS_REPAY_FUNDS': {
        for (const item of items) {
          if (item.cashFlow.value < 0n) {
            const asset = getAsset(item.currency)
            if (!asset) throw new AssetNotFoundException(item.currency)
            actions.push({
              comment: item.type,
              evidence: 'system-rule',
              order: 0,
              loanId: bybitAccountCategory,
              source: `${bybitAccountCategory}_${config.type}_${item.id}`,
              type: 'repayment',
              timestamp: Number(item.transactionTime),
              transfers: [
                {
                  asset,
                  amount: absoluteValue(item.cashFlow),
                  direction: 'out',
                },
              ],
            })
          } else if (item.cashFlow.value > 0n) {
            throw new Error('cashFlow cannot be negative for repayment')
          }
        }
        break
      }
      // airdrop
      case 'AIRDROP': {
        for (const item of items) {
          if (item.cashFlow.value > 0n) {
            const asset = getAsset(item.currency)
            if (!asset) throw new AssetNotFoundException(item.currency)
            actions.push({
              comment: item.type,
              evidence: 'system-rule',
              order: 0,
              source: `${bybitAccountCategory}_${config.type}_${item.id}`,
              type: 'airdrop',
              timestamp: Number(item.transactionTime),
              transfers: [
                {
                  asset,
                  amount: item.cashFlow,
                  direction: 'in',
                },
              ],
            })
          } else if (item.cashFlow.value < 0n) {
            throw new Error('AIRDROP cashFlow cannot be negative')
          }
        }
        break
      }
      // interest
      case 'INTEREST': {
        for (const item of items) {
          if (item.cashFlow.value > 0n) {
            const asset = getAsset(item.currency)
            if (!asset) throw new AssetNotFoundException(item.currency)
            actions.push({
              comment: item.type,
              evidence: 'system-rule',
              order: 0,
              source: `${bybitAccountCategory}_${config.type}_${item.id}`,
              type: 'interest',
              timestamp: Number(item.transactionTime),
              transfers: [
                {
                  asset,
                  amount: item.cashFlow,
                  direction: 'in',
                },
              ],
            })
          } else if (item.cashFlow.value < 0n) {
            throw new Error('INTEREST cashFlow cannot be negative')
          }
        }
        break
      }
      // gift
      case 'BONUS':
      case 'FEE_REFUND':
      case 'TOKENS_REDEMPTION': {
        for (const item of items) {
          if (item.cashFlow.value > 0n) {
            const asset = getAsset(item.currency)
            if (!asset) throw new AssetNotFoundException(item.currency)
            actions.push({
              comment: item.type,
              evidence: 'system-rule',
              order: 0,
              source: `${bybitAccountCategory}_${config.type}_${item.id}`,
              type: 'gift',
              timestamp: Number(item.transactionTime),
              transfers: [
                {
                  asset,
                  amount: item.cashFlow,
                  direction: 'in',
                },
              ],
            })
          } else if (item.cashFlow.value < 0n) {
            throw new Error('cashFlow cannot be negative for gift')
          }
        }
        break
      }
      // evidence = none
      case 'BONUS_RECOLLECT':
      case 'AUTO_DEDUCTION': {
        for (const item of items) {
          if (item.cashFlow.value === 0n) continue
          const asset = getAsset(item.currency)
          if (!asset) throw new AssetNotFoundException(item.currency)
          actions.push({
            comment: item.type,
            evidence: 'none',
            order: 0,
            source: `${bybitAccountCategory}_${config.type}_${item.id}`,
            type: item.cashFlow.value > 0n ? 'gift' : 'transfer',
            timestamp: Number(item.transactionTime),
            transfers: [
              {
                asset,
                amount: absoluteValue(item.cashFlow),
                direction: item.cashFlow.value > 0n ? 'in' : 'out',
              },
            ],
          })
        }
        break
      }
      default: {
        throw new Error(`Unsupported type: ${parsedType}`)
      }
    }
  }

  // timestamp 降順ソート
  const sortedActions = actions.sort((a, b) => b.timestamp - a.timestamp)

  return {
    actions: sortedActions,
    count: response.result.list.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}
